import { customElement, FASTElement, observable, attr } from '@microsoft/fast-element';
import { AllRfqStyles as styles } from './all-rfq.style';
import { AllRfqTemplate as template } from './all-rfq.template';
import { ZeroGridPro as zeroAgGrid } from '@genesislcap/foundation-zero-grid-pro';
import { Auth, logger, Message, SocketSubscription } from '@genesislcap/foundation-comms';
import { INegotiationService, IRfqService, IVppaService } from '../../../../services';
import { IRfq, IRfqLinkedVppas } from '../../../../types';
import { updateArray } from '../../../../utils/dataserver';
import {
  VPPA_RFQ_STATUS,
  NEGOTIATION_STATUS,
  CONTRACT_TYPE,
  PRODUCT_TYPE,
} from '../../../../utils';
import { COLUMN_FIELDS } from './column-config';
import { CASCADE_ENUM, IEnumService } from '../../../../services/enum.service';
import {
  getFormattedDate,
  getPriceValue,
  getPriceRange,
  getStatusTranslate,
  getCreditProfileValue,
} from '../../../../utils/formatting';
import {
  shouldAutosizeByStateKey,
  getGroupColumnSameObject,
} from '../../../../utils/grid';
import { BUYER_DASHBOARD_GRID_TABS } from '../my-portfolio/my-portfolio.template';
import { IUserService } from '../../../../services/user.service';
import { ICompanyService } from '../../../../services/company.service';
import { GridDelegate } from '../../../../utils/grid-delegate';
import { COMPANY_ROLE } from '../../../mf/companys/edit';

interface IRfqGridObject {
  TICKER: string;
  RFQ_ID: string;
  UNIQUE_ID: string;
  RFQ_REFERENCE: string;
  TECHNOLOGY: string;
  NAME: string;
  ISO: string;
  SETTLEMENT_POINT: string;
  VOLUME: number;
  PRICE: number;
  FIXED_PRICE_LOWER_BOUND: number;
  FIXED_PRICE_UPPER_BOUND: number;
  TENOR_START_DATE: number | Date;
  TENOR_MONTHS: number;
  CREDIT_PROFILE: number;
  IMPLIED_CARBON_PRICE: number;
  IMPLIED_CARBON_PRICE_MIN: number;
  IMPLIED_CARBON_PRICE_MAX: number;
  RFQ_APPLICABLE_REC_PROGRAM: string;
  RFQ_CREDIT_PROFILE: string;
  CONTRACT_TYPE: string;
  STATUS: string;
  RFQ_OBJECT: string;
  RFQ_ID_GROUP: string;
  NEGOTIATION_ID: string;
  NEGOTIATION_STATUS: string;
  MODIFIED_AT: number | Date;
  PROJECT_ID: string;
  VPPA_ID: string;
  VPPA_CREDIT_PROFILE: string;
}

export enum ALL_RFQ_TABS {
  ACTIVITY = 'ACTIVITY',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

enum STATE_KEYS {
  TRADING_ACTIVITY = 'buyer-dashboard-trading-activity-grid',
  ACTIVE_RFQS = 'buyer-dashboard-active-rfq-grid',
  INACTIVE_RFQS = 'buyer-dashboard-inactive-rfq-grid',
}

const name = 'all-rfq';
@customElement({
  name,
  template,
  styles,
})
export class AllRfq extends FASTElement {
  @Auth auth: Auth;

  @IRfqService rfqService: IRfqService;
  @IVppaService vppaService: IVppaService;
  @ICompanyService companyService: ICompanyService;
  @INegotiationService negotiationService: INegotiationService;
  @IEnumService enumService: IEnumService;
  @IUserService userService: IUserService;

  @observable rfqGrid: zeroAgGrid;
  @observable rfqVppaCriteria: { rfqCriteria: string; vppaNegotiationCriteria: string };

  @observable rfqSubscription: SocketSubscription;
  @observable negotiationSubscription: SocketSubscription;
  @observable rfqList: IRfq[];
  @observable vppaList: IRfqLinkedVppas[];
  @observable gridData: IRfqGridObject[] = [];

  @attr projectId: string;
  @attr @observable activeTabId: BUYER_DASHBOARD_GRID_TABS;
  @observable negotiations: any;
  @observable currentStateKey: STATE_KEYS;

  private gridDelegate: GridDelegate;

  private rfqTabs: BUYER_DASHBOARD_GRID_TABS[] = [
    BUYER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB,
    BUYER_DASHBOARD_GRID_TABS.ACTIVE_RFQS_TAB,
    BUYER_DASHBOARD_GRID_TABS.INACTIVE_RFQS_TAB,
  ];

  connectedCallback() {
    super.connectedCallback();

    const gridOptions = {
      onRowClicked: (row) => this.rowSelection(row),
      onRowDoubleClicked: (row) => this.rowDoubleClicked(row),
      onFirstDataRendered: () => this.firstDataRendered(),
      suppressCellFocus: true,
      animateRows: true,
      groupSelectsChildren: true,
      groupAllowUnbalanced: true,
      suppressBrowserResizeObserver: true,
      scrollbarWidth: 10,
      defaultColDef: {
        resizable: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
      },
      tooltipShowDelay: 300,
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivotMode: true,
            },
          },
        ],
      },
    };
    this.rfqGrid.gridOptions = gridOptions;
  }

  private rowDoubleClicked(row) {
    if (row.data?.VPPA_ID) {
      this.vppaService.openVppaDetailModal(row.data?.VPPA_ID);
    } else if (row.data?.RFQ_ID) {
      this.rfqService.openRfqDetailModal(row.data?.RFQ_ID);
    } else if (row.node?.groupData[0]) {
      this.rfqService.openRfqDetailModal(row.node?.groupData[0]);
    }
  }

  public rfqVppaCriteriaChanged(
    oldValue: { rfqCriteria: string; vppaNegotiationCriteria: string },
    newValue: { rfqCriteria: string; vppaNegotiationCriteria: string },
  ) {
    const rfqCriteriaMatch = oldValue?.rfqCriteria == newValue?.rfqCriteria;
    const vppaNegotiationCriteriaMatch =
      oldValue?.vppaNegotiationCriteria == newValue?.vppaNegotiationCriteria;
    if (!rfqCriteriaMatch || !vppaNegotiationCriteriaMatch) {
      // only refresh if the criteria aren't the same, we don't want to re-fetch the data
      this.rfqList = [];
      this.vppaList = [];
      this.getAllRfqs(newValue);
      this.getLinkedVppas(newValue);
    }
  }

  public onGridReady() {
    this.rfqGrid.gridApi.setRowData(this.gridData);

    // If there is a current state key (aka the user made changes to the grid), we want to override the autosize
    this.gridDelegate = new GridDelegate(this.rfqGrid, this.currentStateKey);
    this.setColumnVisibility(COLUMN_FIELDS.CONTRACT_TYPE, false);
    this.rfqVppaCriteria = {
      rfqCriteria: `NEGOTIATION_ID != null && (RFQ_STATUS == '${VPPA_RFQ_STATUS.ACTIVE}' || RFQ_STATUS == '${VPPA_RFQ_STATUS.TRADE_PENDING}' ||  RFQ_STATUS == '${VPPA_RFQ_STATUS.EXISTING_INTEREST}') && NEGOTIATION_STATUS != '${NEGOTIATION_STATUS.REJECTED}'`,
      vppaNegotiationCriteria: `BUYER_VISIBILITY == true && (VPPA_STATUS == '${VPPA_RFQ_STATUS.ACTIVE}' || VPPA_STATUS == '${VPPA_RFQ_STATUS.TRADE_PENDING}' || VPPA_STATUS == '${VPPA_RFQ_STATUS.EXISTING_INTEREST}') &&
        (NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.IN_PROGRESS}' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.REQUIRES_CONFIRMATION}' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.CONFIRMED_AWAITING_BUYER}' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.CONFIRMED_AWAITING_SELLER}' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.SUCCESSFUL}') && NEGOTIATION_STATUS != '${NEGOTIATION_STATUS.REJECTED}'
      `,
    };
    this.updateGridCsv();
  }

  private getAllRfqs(newCriteria) {
    let resultList = [];
    this.rfqSubscription?.unsubscribe();
    let matchCriteria = '';
    if (!(this.companyService.getCurrentCompany().COMPANY_ROLE === COMPANY_ROLE.CASCADE_ADMIN)) {
      matchCriteria =
        "CASCADE_COMPANY_ID == '" +
        this.companyService.getCurrentCompany().CASCADE_COMPANY_ID +
        "' && ";
    }

    if (newCriteria && newCriteria.rfqCriteria) {
      matchCriteria = matchCriteria.concat(newCriteria.rfqCriteria);
      this.rfqSubscription = this.rfqService.getAllRfqs(matchCriteria).subscribe({
        next: (streamMessage: Message) => {
          resultList = streamMessage.ROW ?? null;
          if (resultList) {
            this.rfqList = updateArray(resultList, this.rfqList).sort(
              (a, b) => b.MODIFIED_AT - a.MODIFIED_AT,
            );
          } else {
            this.rfqList = [];
          }
          this.updateGrid();
        },
        error: (error: any) => {
          this.rfqList = [];
          logger.error(error);
        },
      });
    }
  }

  private getLinkedVppas(newCriteria) {
    let resultList = [];
    this.vppaList = [];

    if (newCriteria && newCriteria.vppaNegotiationCriteria) {
      const vppaNegotiationCriteria =
        this.companyService.getCurrentCompany().COMPANY_ROLE === COMPANY_ROLE.CASCADE_ADMIN
          ? newCriteria.vppaNegotiationCriteria
          : `BUYER_COMPANY_ID == '${this.companyService.getCurrentCompany().CASCADE_COMPANY_ID}' && `.concat(
              newCriteria.vppaNegotiationCriteria,
            );
      this.negotiationSubscription?.unsubscribe();
      this.negotiationSubscription = this.negotiationService
        .getNegotiationVppas({
          CRITERIA_MATCH: vppaNegotiationCriteria,
        })
        .subscribe({
          next: (streamMessage: Message) => {
            resultList = streamMessage.ROW ?? null;
            if (resultList) {
              resultList.forEach((obj) => {
                if (obj.APPLICABLE_REC_PROGRAM !== undefined) {
                  obj.RFQ_APPLICABLE_REC_PROGRAM = obj.APPLICABLE_REC_PROGRAM;
                  delete obj.APPLICABLE_REC_PROGRAM;
                }
              });
              this.vppaList = updateArray(resultList, this.vppaList).sort(
                (a, b) => b.MODIFIED_AT - a.MODIFIED_AT,
              );
            } else {
              this.vppaList = [];
            }
            this.updateGrid();
          },
          error: (error: any) => {
            logger.error(error);
          },
        });
    }
  }

  private updateGrid() {
    this.gridData = [];
    const rfqMap = new Map();
    const rfqElmMap = new Map();

    this.rfqList.forEach((element) => {
      rfqMap.set(element.RFQ_ID, false);
      rfqElmMap.set(element.RFQ_ID, element);
    });

    if (
      this.activeTabId === BUYER_DASHBOARD_GRID_TABS.INACTIVE_RFQS_TAB ||
      this.activeTabId === BUYER_DASHBOARD_GRID_TABS.ACTIVE_RFQS_TAB
    ) {
      this.gridData = [];
    } else {
      this.vppaList.forEach((element) => {
        if (rfqMap.has(element.RFQ_ID)) {
          rfqMap.set(element.RFQ_ID, true);
          const rfq = rfqElmMap.get(element.RFQ_ID);
          this.gridData.push({
            TICKER: element.TICKER,
            RFQ_ID: element.RFQ_ID,
            UNIQUE_ID: element.UNIQUE_ID,
            RFQ_REFERENCE: null,
            TECHNOLOGY: element.TECHNOLOGY,
            NAME: element.NAME,
            ISO: element.ISO,
            SETTLEMENT_POINT: element.SETTLEMENT_POINT,
            VOLUME: element.VOLUME,
            PRICE: element.FIXED_PRICE,
            FIXED_PRICE_LOWER_BOUND: null,
            FIXED_PRICE_UPPER_BOUND: null,
            TENOR_START_DATE: element.TENOR_START_DATE,
            TENOR_MONTHS: element.TENOR_MONTHS,
            RFQ_APPLICABLE_REC_PROGRAM: element.RFQ_APPLICABLE_REC_PROGRAM,
            CREDIT_PROFILE: element.SELLER_POST_COD_COLLATERAL,
            IMPLIED_CARBON_PRICE: element.IMPLIED_CARBON_PRICE,
            IMPLIED_CARBON_PRICE_MIN: null,
            IMPLIED_CARBON_PRICE_MAX: null,
            CONTRACT_TYPE: element.CONTRACT_TYPE,
            STATUS: null,
            RFQ_OBJECT: rfq,
            RFQ_CREDIT_PROFILE: element.RFQ_CREDIT_PROFILE,
            RFQ_ID_GROUP: element.RFQ_ID,
            NEGOTIATION_ID: element.NEGOTIATION_ID,
            NEGOTIATION_STATUS: element.NEGOTIATION_STATUS,
            PROJECT_ID: element['VPPA_PROJECT_ID'],
            MODIFIED_AT: element.MODIFIED_AT,
            VPPA_ID: element.VPPA_ID,
            VPPA_CREDIT_PROFILE: element.VPPA_CREDIT_PROFILE,
          });
        }
      });
    }

    this.rfqList.forEach((element) => {
      if (!rfqMap.get(element.RFQ_ID)) {
        this.gridData.push({
          TICKER: element.TICKER,
          RFQ_ID: element.RFQ_ID,
          UNIQUE_ID: element.UNIQUE_ID,
          RFQ_REFERENCE: element.RFQ_REFERENCE,
          TECHNOLOGY: element.TECHNOLOGY,
          NAME: null,
          ISO: element.ISO,
          SETTLEMENT_POINT: element.SETTLEMENT_POINT,
          VOLUME:
            element.CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED
              ? element.CAPACITY
              : element.REQUESTED_VOLUME,
          PRICE: element.FIXED_PRICE,
          FIXED_PRICE_LOWER_BOUND: element.FIXED_PRICE_LOWER_BOUND,
          FIXED_PRICE_UPPER_BOUND: element.FIXED_PRICE_UPPER_BOUND,
          TENOR_START_DATE: element.TENOR_START_DATE,
          TENOR_MONTHS: element.TENOR_MONTHS,
          RFQ_APPLICABLE_REC_PROGRAM: element.RFQ_APPLICABLE_REC_PROGRAM,
          CREDIT_PROFILE: element.BUYER_POST_COD_COLLATERAL,
          IMPLIED_CARBON_PRICE: null,
          IMPLIED_CARBON_PRICE_MIN: element.IMPLIED_CARBON_PRICE_MIN,
          IMPLIED_CARBON_PRICE_MAX: element.IMPLIED_CARBON_PRICE_MAX,
          CONTRACT_TYPE: element.CONTRACT_TYPE,
          STATUS: element.RFQ_STATUS,
          RFQ_OBJECT: '',
          RFQ_ID_GROUP: null,
          NEGOTIATION_ID: null,
          NEGOTIATION_STATUS: null,
          RFQ_CREDIT_PROFILE: element.RFQ_CREDIT_PROFILE,
          PROJECT_ID: null,
          MODIFIED_AT: element.MODIFIED_AT,
          VPPA_ID: '',
          VPPA_CREDIT_PROFILE: '',
        });
      }
    });

    this.rfqGrid.gridApi.setRowData(this.gridData);
    this.rfqGrid.gridApi.refreshHeader();

    if (this.activeTabId === BUYER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB) {
      this.rfqGrid.gridApi.expandAll();
      this.cleanRecentActivity();
    } else {
      this.rfqGrid.gridApi.collapseAll();
    }
    this.updateGridCsv();
  }

  public setColumnVisibility(column, show: boolean) {
    this.rfqGrid.columnApi.setColumnVisible(column, show);
  }

  // Think this is to remove any RFQs that dont have a valid negotiation linked to it
  private cleanRecentActivity() {
    const itemsToRemove = [];
    this.rfqGrid.gridApi.forEachNode((node) => {
      if (node.data) {
        if (node.data.RFQ_OBJECT == '') {
          const rowData = node.data;
          itemsToRemove.push(rowData);
        }
      }
    });
    this.rfqGrid.gridApi.applyTransaction({ remove: itemsToRemove });
  }

  public downloadGridCsv() {
    const csvParams = {
      fileName: this.activeTabId,
      processCellCallback: (params) => {
        let formattedValue = params.value;
        if (params.column.getColId() === COLUMN_FIELDS.TICKER) {
          formattedValue = JSON.parse(params.value).NAME;
        } else if (params.column.getColId() === COLUMN_FIELDS.UNIQUE_ID) {
          formattedValue = params.value
            ? params.value
            : getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).UNIQUE_ID;
        } else if (params.column.getColId() === COLUMN_FIELDS.RFQ_REFERENCE) {
          formattedValue = params.data
            ? params.node.data
            : getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).RFQ_REFERENCE;
        } else if (params.column.getColId() === COLUMN_FIELDS.CONTRACT_TYPE) {
          formattedValue = params.value
            ? this.enumService.getEnumTranslation(CASCADE_ENUM.CONTRACT_TYPE, params.value)
            : this.enumService.getEnumTranslation(
                CASCADE_ENUM.CONTRACT_TYPE,
                getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).CONTRACT_TYPE,
              );
        } else if (params.column.getColId() === COLUMN_FIELDS.TECHNOLOGY) {
          formattedValue = params.value
            ? params.value
            : getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).TECHNOLOGY;
        } else if (params.column.getColId() === COLUMN_FIELDS.ISO) {
          formattedValue = params.value
            ? params.value
            : getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).ISO;
        } else if (params.column.getColId() === COLUMN_FIELDS.SETTLEMENT_POINT) {
          formattedValue = params.value
            ? params.value
            : getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).SETTLEMENT_POINT;
        } else if (params.column.getColId() === COLUMN_FIELDS.PRICE) {
          if (this.activeTabId === BUYER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB) {
            if (params.node.data) {
              if (params.node.data.PRICE) {
                formattedValue = getPriceValue(params.node.data.PRICE, '', '$');
              } else {
                if (params.node.data.PRODUCT_TYPE === PRODUCT_TYPE.RECS) {
                  formattedValue = params.node.data.FIXED_PRICE;
                } else {
                  formattedValue = getPriceValue(
                    (params.data.FIXED_PRICE_LOWER_BOUND + params.data.FIXED_PRICE_UPPER_BOUND) / 2,
                    '',
                    '$',
                  );
                }
              }
            } else {
              if (
                getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).PRODUCT_TYPE ===
                PRODUCT_TYPE.RECS
              ) {
                formattedValue = getPriceValue(
                  getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).FIXED_PRICE,
                  '',
                  '$',
                );
              } else {
                const fixedPriceLowerBound = getGroupColumnSameObject(
                  params,
                  COLUMN_FIELDS.RFQ_OBJECT,
                ).FIXED_PRICE_LOWER_BOUND;
                const fixedPriceUpperBound = getGroupColumnSameObject(
                  params,
                  COLUMN_FIELDS.RFQ_OBJECT,
                ).FIXED_PRICE_UPPER_BOUND;
                formattedValue = getPriceValue(
                  (fixedPriceLowerBound + fixedPriceUpperBound) / 2,
                  '',
                  '$',
                );
              }
            }
          } else {
            if (params.node.data) {
              if (params.node.data.PRICE) {
                formattedValue = getPriceValue(params.node.data.PRICE, '', '$');
              } else {
                if (params.node.data.PRODUCT_TYPE === PRODUCT_TYPE.RECS) {
                  formattedValue = params.node.data.FIXED_PRICE;
                } else {
                  formattedValue = getPriceRange(
                    getPriceValue(params.node.data.FIXED_PRICE_LOWER_BOUND),
                    getPriceValue(params.node.data.FIXED_PRICE_UPPER_BOUND),
                  );
                }
              }
            } else {
              if (
                getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).PRODUCT_TYPE ===
                PRODUCT_TYPE.RECS
              ) {
                formattedValue = getPriceValue(
                  getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).FIXED_PRICE,
                );
              } else {
                formattedValue = getPriceRange(
                  getPriceValue(
                    getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT)
                      .FIXED_PRICE_LOWER_BOUND,
                  ),
                  getPriceValue(
                    getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT)
                      .FIXED_PRICE_UPPER_BOUND,
                  ),
                );
              }
            }
          }
        } else if (params.column.getColId() === COLUMN_FIELDS.IMPLIED_CARBON_PRICE) {
          if (params.value) {
            formattedValue = '$' + params.value;
          } else if (params.data) {
            formattedValue = getPriceRange(
              params.data.IMPLIED_CARBON_PRICE_MIN,
              params.data.IMPLIED_CARBON_PRICE_MAX,
            );
          } else {
            formattedValue = getPriceRange(
              getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT)
                .IMPLIED_CARBON_PRICE_MIN,
              getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT)
                .IMPLIED_CARBON_PRICE_MAX,
            );
          }
        } else if (params.column.getColId() === COLUMN_FIELDS.VOLUME) {
          formattedValue = params.value
            ? params.value
            : getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).REQUESTED_VOLUME;
        } else if (params.column.getColId() === COLUMN_FIELDS.TENOR_START_DATE) {
          formattedValue = params.value
            ? getFormattedDate(params.value)
            : getFormattedDate(
                params.node.allLeafChildren[0].data[COLUMN_FIELDS.RFQ_OBJECT].TENOR_START_DATE,
              );
        } else if (params.column.getColId() === COLUMN_FIELDS.TENOR_MONTHS) {
          if (params.value || params.value === 0) {
            formattedValue = params.value;
          } else if (params.node.allLeafChildren[0]) {
            formattedValue =
              params.node.allLeafChildren[0].data[COLUMN_FIELDS.RFQ_OBJECT].TENOR_MONTHS;
          } else {
            formattedValue = '';
          }
        } else if (params.column.getColId() === COLUMN_FIELDS.STATUS) {
          formattedValue = params.data
            ? getStatusTranslate(params.data.STATUS)
            : getStatusTranslate(
                getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).RFQ_STATUS,
              );
        } else if (params.column.getColId() === COLUMN_FIELDS.CREDIT_PROFILE) {
          let groupedCreditProfile = getGroupColumnSameObject(
            params,
            COLUMN_FIELDS.RFQ_OBJECT,
          ).RFQ_CREDIT_PROFILE;
          let groupedCollateral = getGroupColumnSameObject(
            params,
            COLUMN_FIELDS.RFQ_OBJECT,
          ).BUYER_POST_COD_COLLATERAL;
          formattedValue = params.value
            ? getCreditProfileValue(groupedCreditProfile, params.value)
            : getCreditProfileValue(groupedCreditProfile, groupedCollateral);
        } else if (params.column.getColId() === COLUMN_FIELDS.ACTIONS) {
          formattedValue = params.data
            ? params.data.NEGOTIATION_STATUS
            : getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).NEGOTIATION_STATUS;
        }
        return formattedValue;
      },
    };
    this.rfqGrid.gridApi.exportDataAsCsv(csvParams);
  }

  private updateGridCsv() {
    this.rfqGrid.gridApi.getDataAsCsv();
  }

  private rowSelection(row) {
    if (row.data?.VPPA_ID) {
      this.$emit('vppaFromLinkedRfq', row.data?.VPPA_ID);
    } else if (row.node?.allLeafChildren) {
      if (row.node?.allLeafChildren.length > 0) {
        const firstLinkedVppa = row.node.allLeafChildren[0];
        this.$emit('vppaFromLinkedRfq', firstLinkedVppa.data.VPPA_ID);
      }
    }
  }

  private firstDataRendered() {
    this.rfqGrid.gridApi.getRenderedNodes()[0]?.setSelected(true);
    this.rowSelection({
      node: this.rfqGrid.gridApi.getRenderedNodes()[0],
    });

    // Check on first render of data (If you check on grid ready, the columns will be squished because there is no data yet)
    this.checkIfShouldAutosize();
  }

  private checkIfShouldAutosize() {
    if (this.rfqTabs.includes(this.activeTabId)) {
      const shouldAutosize = shouldAutosizeByStateKey(this.currentStateKey);
      if (shouldAutosize) {
        this.rfqGrid?.columnApi.autoSizeAllColumns();
      }
    }
  }

  public activeTabIdChanged() {
    this.getStateKey();
  }

  private getStateKey() {
    switch (this.activeTabId) {
      case BUYER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB:
        this.currentStateKey = STATE_KEYS.TRADING_ACTIVITY;
        break;
      case BUYER_DASHBOARD_GRID_TABS.ACTIVE_RFQS_TAB:
        this.currentStateKey = STATE_KEYS.ACTIVE_RFQS;
        break;
      case BUYER_DASHBOARD_GRID_TABS.INACTIVE_RFQS_TAB:
        this.currentStateKey = STATE_KEYS.INACTIVE_RFQS;
        break;
    }
    this.gridDelegate?.changeState(this.currentStateKey, true);
    this.checkIfShouldAutosize();
  }
}
